import React, { useEffect, useMemo, useState, Fragment } from "react";
import { Modal, Table, Button, Alert, Space, Tooltip, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import request from "superagent";

import { getAuthHeader } from "../../utils/superagent";
import { showErrorToast, showSuccessToast } from "@src/appV2/lib/Notifications";
import { ROLE_MAPPINGS } from "../teamMembers/constants";
import { getHighestAccessRole } from "../teamMembers/utils";
import { environmentConfig } from "@src/appV2/environment";
import { NodeEnvironment } from "@src/appV2/environment/types";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { useImpersonationSessions } from "@src/appV2/ImpersonationSessions/ImpersonationSessionsProvider";
import { useEffectivePermissions } from "@src/appV2/Auth/hooks/useEffectivePermissions";

interface ActiveUser {
  _id: string;
  name: string;
  email: string;
  roles?: string[];
}

function AutoLoginLinksModal({ visible, onCancel, facilityId }) {
  const [users, setUsers] = useState<ActiveUser[]>([]);
  const [isLoading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [link, setLink] = useState(null);
  const [nameFilter, setNameFilter] = useState("");

  const isWorkplaceCognitoImpersonationEnabled = useCbhFlag(
    CbhFeatureFlag.WORKPLACE_COGNITO_IMPERSONATION,
    { defaultValue: false }
  );
  const { openSession } = useImpersonationSessions();
  const { canImpersonateUsers } = useEffectivePermissions();

  const isDevelopment =
    environmentConfig.REACT_APP_ENVIRONMENT_NAME === NodeEnvironment.DEVELOPMENT;

  useEffect(() => {
    if (!visible) {
      return;
    }
    setNameFilter("");
    const getActiveUsers = async () => {
      void request
        .get(`${global.api}/facilityUser/activeUsers`)
        .query({ facilityId })
        .set(await getAuthHeader())
        .then(({ body }) => {
          setUsers(body.users);
          setLoading(false);
        });
    };
    setLoading(true);
    void getActiveUsers();
  }, [facilityId, visible]);

  const filteredUsers = useMemo(() => {
    const sorted = [...users].sort((a, b) =>
      (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" })
    );
    const term = nameFilter.trim().toLowerCase();
    if (!term) {
      return sorted;
    }
    return sorted.filter((user) => user.name?.toLowerCase().includes(term));
  }, [users, nameFilter]);

  const fetchLink = (email) => async () => {
    setSelectedUser(email);
    const appDomain = `https://${window.location.hostname}`;

    let url = window.location.origin;
    if (window.location?.pathname?.startsWith("/pr-")) {
      const prMatch = window.location.pathname.match(/^\/pr-\d+/);
      if (prMatch) {
        url += prMatch[0];
      }
    }
    url += "/login/verify";

    const query = {
      email,
      appDomain,
      ...(isDevelopment ? { url } : {}),
    };

    request
      .get(`${global.api}/facilityUser/signInEmailLink`)
      .query(query)
      .set(await getAuthHeader())
      .then(({ body }) => {
        // FIXME: Automatically disabled during ESLint config update
        // eslint-disable-next-line @typescript-eslint/no-shadow
        const { link } = body;
        if (navigator.clipboard) {
          void navigator.clipboard.writeText(link);
          showSuccessToast("Auto Login Link copied to clipboard");
        } else {
          setLink(link);
          setShowLinkModal(true);
        }
        setSelectedUser(null);
      })
      .catch((err) => {
        if (err.response) {
          const { text } = err.response;
          showErrorToast(text);
        } else {
          showErrorToast(err.message);
        }
        setSelectedUser(null);
      });
  };

  const handleImpersonate = (user: { _id: string; name: string; email: string }) => {
    if (!canImpersonateUsers) {
      return;
    }

    openSession(
      "workplace",
      environmentConfig.REACT_APP_WORKPLACE_APP_URL,
      user._id,
      user.name ?? user.email,
      user.email
    );
    onCancel();
  };

  const renderActionButtons = (email, record) => {
    if (isWorkplaceCognitoImpersonationEnabled) {
      return (
        <Space size="small" style={{ whiteSpace: "nowrap" }}>
          <Tooltip
            title={canImpersonateUsers ? "" : "You don't have permission to impersonate users."}
          >
            <Button
              size="small"
              type="primary"
              disabled={!canImpersonateUsers}
              onClick={() => handleImpersonate(record)}
            >
              Impersonate
            </Button>
          </Tooltip>
          <Button
            size="small"
            onClick={fetchLink(email)}
            loading={email === selectedUser}
            style={{ opacity: 0.6 }}
          >
            Copy link (legacy)
          </Button>
        </Space>
      );
    }

    return (
      <Button type="primary" onClick={fetchLink(email)} loading={email === selectedUser}>
        Click to copy link
      </Button>
    );
  };

  const renderRole = (roles: string[] | undefined) => {
    return ROLE_MAPPINGS[getHighestAccessRole(roles, true)]?.label;
  };

  const handleCloseLinkModal = () => {
    setLink(null);
    setShowLinkModal(false);
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      ellipsis: true,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      ellipsis: true,
    },
    {
      title: "Role",
      dataIndex: "roles",
      render: renderRole,
      key: "roles",
      width: 120,
    },
    {
      title: "Actions",
      dataIndex: "email",
      key: "_id",
      render: renderActionButtons,
      width: isWorkplaceCognitoImpersonationEnabled ? 250 : 160,
    },
  ];

  return (
    <Fragment>
      <Modal
        title="Auto Login Links"
        visible={visible}
        width={800}
        onCancel={onCancel}
        footer={null}
      >
        <Alert
          style={{ marginBottom: "10px" }}
          message={`Please open the login link in incognito browser or log out of your account first.`}
          type="info"
          showIcon
        />
        {isWorkplaceCognitoImpersonationEnabled && !canImpersonateUsers && (
          <Alert
            style={{ marginBottom: "10px" }}
            message="You need the Impersonation role to impersonate workplace users. Please contact an administrator to request access."
            type="error"
            showIcon
          />
        )}
        <Input
          placeholder="Filter by name"
          prefix={<SearchOutlined />}
          value={nameFilter}
          onChange={(event) => setNameFilter(event.target.value)}
          allowClear
          style={{ marginBottom: 10, maxWidth: 400 }}
        />
        <Table
          rowKey="_id"
          dataSource={filteredUsers}
          loading={isLoading}
          columns={columns}
          pagination={false}
        />
      </Modal>
      <Modal
        title="Oops, unable to copy link to clipboard"
        visible={showLinkModal}
        onCancel={handleCloseLinkModal}
        footer={null}
      >
        <p style={{ marginBottom: "10px" }}>Please copy the following link:</p>
        <p>{link}</p>
      </Modal>
    </Fragment>
  );
}

export { AutoLoginLinksModal };
